import { useEffect, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { FiExternalLink } from "react-icons/fi";
import { apiV1 } from "api-v2";

import AccordionLargeTextDisplay from "newui/components/AccordionLargeTextDisplay";
import { ImageCarousel } from "../ImageCarousel";

import type { PastProject } from "newui/features/Company/types";
import { PROFILE_TYPE } from "../../types";
import PillDisplay from "newui/components/PillDisplay";

interface MobilePrimaryProjectCardProps {
	profileTypeId: number;
	project: PastProject;
	/** "card" adds border/shadow wrapper; "flat" renders without outer chrome (for use inside modals) */
	variant?: "card" | "flat";
}

const DetailField: React.FC<{
	label: string;
	value: string | number | null | undefined;
}> = ({ label, value }) => {
	if (!value) return null;
	return (
		<div>
			<dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
				{label}
			</dt>
			<dd className="mt-0.5 text-sm text-gray-900">{value}</dd>
		</div>
	);
};

/**
 * Full-detail project card for "primary" (featured) projects on mobile.
 * Renders all project fields inline — images, summary, key figures,
 * awards, innovation, testimonials, location — without opening a modal.
 */
export const MobilePrimaryProjectCard: React.FC<
	MobilePrimaryProjectCardProps
> = ({ project, profileTypeId, variant = "card" }) => {
	const [fullProject, setFullProject] = useState<PastProject | null>(null);
	const [isLoading, setIsLoading] = useState(false);

	const isSubcontractor = profileTypeId === PROFILE_TYPE.TRADE;

	// Fetch full project detail on mount (list data doesn't include summary, awards, etc.)
	useEffect(() => {
		if (!project?.id) return;

		let cancelled = false;
		setIsLoading(true);

		apiV1
			.get(`/past-projects/${project.id}`)
			.then((response) => {
				if (!cancelled) {
					const data = response.data?.data ?? response.data;
					setFullProject(data);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setFullProject(project);
				}
			})
			.finally(() => {
				if (!cancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [project?.id]);

	const displayProject = fullProject ?? project;

	const locationStr = displayProject.location
		? typeof displayProject.location === "string"
			? displayProject.location
			: (displayProject.location as any).value ??
				[
					displayProject.location.address,
					displayProject.location.city,
					displayProject.location.state,
					displayProject.location.postcode,
				]
					.filter(Boolean)
					.join(", ")
		: "";

	return (
		<div className={variant === "card" ? "overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm" : ""}>
			{/* Images with project name & location overlay */}
			{displayProject.images && displayProject.images.length > 0 ? (
				<ImageCarousel
					images={displayProject.images}
					alt={displayProject.name}
					heading={displayProject.name}
					subheading={locationStr}
					height="h-56"
				/>
			) : (
				<div className="relative flex h-40 items-center justify-center bg-gray-100">
					<span className="text-sm text-gray-400">No image</span>
					{/* Still show name/location overlay even without images */}
					<div className="absolute bottom-4 left-4">
						<p className="text-lg font-semibold leading-tight text-gray-900">
							{displayProject.name}
						</p>
						{locationStr && (
							<p className="mt-0.5 text-sm text-gray-500">{locationStr}</p>
						)}
					</div>
				</div>
			)}

			<div className="space-y-4 p-4">

				{/* Sector tags */}

				{displayProject.sectors && displayProject.sectors.length > 0 && (
					<div>
						<h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
							Scope
						</h5>
						<div className="flex flex-wrap gap-1">
							{displayProject.sectors.map((sector) => (
								<PillDisplay name={sector.name} key={sector.id} type="sector" />
							))}
						</div>
					</div>
				)}

				{/* Loading state for detail fields */}
				{isLoading ? (
					<div className="flex items-center justify-center py-4">
						<Spinner size="sm" />
						<span className="ml-2 text-xs text-gray-400">
							Loading details...
						</span>
					</div>
				) : (
					<>
						{/* Summary */}
						<AccordionLargeTextDisplay
							title="Summary"
							text={displayProject.summary}
							isLoading={false}
							maxTextDisplay={200}
						/>

						{/* Key figures */}
						<dl className="grid grid-cols-2 gap-3">
							<DetailField
								label="Principal/Owner"
								value={displayProject.client_name}
							/>
							<DetailField
								label={
									isSubcontractor
										? "Trade Packages Value (AUD)"
										: "Construction Value (AUD)"
								}
								value={
									isSubcontractor
										? displayProject.trade_value
										: displayProject.value_humanized
								}
							/>
							<DetailField
								label="Year Completed"
								value={displayProject.year_completed}
							/>
							<DetailField
								label={
									isSubcontractor
										? "Trade Package/s"
										: "Procurement Model"
								}
								value={
									isSubcontractor
										? displayProject.trades
											?.map(
												(t: { name: string }) => t.name
											)
											.join(", ")
										: displayProject.procurement_methods
											?.map((p) => p.name)
											.join(", ")
								}
							/>
							<DetailField
								label="Head Contractor"
								value={displayProject.head_contractor}
							/>
							<DetailField
								label="Company Role"
								value={displayProject.company_role}
							/>
							<DetailField
								label="Gross Floor Area (m2)"
								value={displayProject.gross_floor_area}
							/>
							<DetailField
								label="Site Area"
								value={displayProject.site_area}
							/>
							<DetailField
								label="Funding Type"
								value={displayProject.funding_type}
							/>
						</dl>

						{/* Awards */}
						<AccordionLargeTextDisplay
							title="Awards"
							text={displayProject.awards}
							isLoading={false}
							maxTextDisplay={200}
						/>

						{/* Innovation */}
						<AccordionLargeTextDisplay
							title="Innovation & Value Summary"
							text={displayProject.innovation_value_summary}
							isLoading={false}
							maxTextDisplay={200}
						/>

						{/* Testimonials — always a URL */}
						{displayProject.testimonials && (
							<a
								href={displayProject.testimonials}
								target="_blank"
								rel="noopener noreferrer"
								className="inline-flex items-center gap-2 rounded border border-blue-200 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50"
							>
								<FiExternalLink size={14} />
								View Client Testimonial
							</a>
						)}
					</>
				)}
			</div>
		</div>
	);
};
